import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(",", ".")));

export const PriorGradesDialog = ({ open, onOpenChange, study, onSave }) => {
  const { scopeParams } = useAuth();
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    const saved = study?.subjects || [];
    api.get("/subjects", { params: scopeParams }).then((r) => {
      const names = new Set(saved.map((s) => s.subject.toLowerCase()));
      const extra = r.data.filter((s) => !names.has(s.name.toLowerCase())).map((s) => ({ subject: s.name, grade: "", workload_hours: s.workload_hours ?? "" }));
      setRows([...saved.map((s) => ({ ...s, grade: s.grade ?? "", workload_hours: s.workload_hours ?? "" })), ...extra]);
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  const save = () => {
    const out = rows.filter((r) => r.subject.trim() && (r.grade !== "" || r.workload_hours !== ""))
      .map((r) => ({ subject: r.subject.trim(), grade: num(r.grade), workload_hours: num(r.workload_hours) }));
    if (out.some((r) => r.grade !== null && (isNaN(r.grade) || r.grade < 0 || r.grade > 10))) return toast.error("Notas devem estar entre 0 e 10");
    if (out.some((r) => r.workload_hours !== null && (isNaN(r.workload_hours) || r.workload_hours < 0))) return toast.error("Carga horária inválida");
    onSave(out.map((r) => ({ ...r, workload_hours: r.workload_hours === null ? null : Math.round(r.workload_hours) })));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="prior-grades-dialog">
        <DialogHeader><DialogTitle>Notas da {study?.grade || "série"} {study?.year ? `(${study.year})` : ""}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Nota de 0 a 10 e carga horária (h). Disciplinas em branco não saem no histórico.</p>
        <div className="grid grid-cols-[1fr_80px_80px_32px] gap-2 text-xs font-semibold text-slate-500"><span>Disciplina</span><span>Nota</span><span>C/H</span><span /></div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[1fr_80px_80px_32px] items-center gap-2" data-testid={`prior-grade-row-${i}`}>
            <Input value={r.subject} onChange={(e) => upd(i, "subject", e.target.value)} data-testid={`prior-grade-subject-${i}`} />
            <Input type="number" step="0.1" min={0} max={10} value={r.grade} onChange={(e) => upd(i, "grade", e.target.value)} data-testid={`prior-grade-value-${i}`} />
            <Input type="number" min={0} value={r.workload_hours} onChange={(e) => upd(i, "workload_hours", e.target.value)} data-testid={`prior-grade-ch-${i}`} />
            <Button type="button" size="icon" variant="ghost" className="text-rose-600" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={() => setRows((rs) => [...rs, { subject: "", grade: "", workload_hours: "" }])} data-testid="prior-grade-add"><Plus className="mr-1 h-4 w-4" /> Disciplina</Button>
        <DialogFooter><Button onClick={save} data-testid="prior-grades-save">Aplicar notas</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
